import './DeviceModal.css';

import { checkmark, informationCircleOutline } from 'ionicons/icons';
// TODO replace with Apple-Maps on iOS/ Google-Maps on Android
import React, {
  useState,
  useEffect,
  useCallback,
  ComponentProps,
  useReducer,
  useRef,
} from 'react';
import { useHistory } from 'react-router';

import {
  IonAccordion,
  IonAccordionGroup,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonModal,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  useIonAlert,
  useIonToast,
  useIonViewDidEnter,
} from '@ionic/react';
import { IValueDidChange, Lambda, runInAction, toJS, observe } from 'mobx';
import { observer } from 'mobx-react-lite';
import useLocation from 'pages/Record/useLocation';
import Trans from 'util/Trans';
import { useStores } from '../../models';
import {
  DongleStatusResponse,
  useCalculationsAPI,
} from 'services/freak_estimator/Estimator';
import { useFSM } from '../../services/DongleController/DongleController';
import { BluetoothEntry, BluetoothInfo } from '../../models/BluetoothInfo';
import { ST } from '../../types';
import {
  AccordionGroupChangeEventDetail,
  IonAccordionGroupCustomEvent,
} from '@ionic/core';
import SensorCard from 'pages/Record/components/SensorCard';
import { NAME_FREAK } from 'spinnaxBase/constants';
import { StatesBLE } from 'services/BLE/BLE';
import DynamicButton from 'components/buttons/DynamicButton';
import { MESSAGE_ALIVE_MS, MESSAGE_POSITION } from 'util/constants';

type DongleStatusType = DongleStatusResponse['text'];

interface BluetoothEntryAction {
  cmd: 'ADD' | 'REMOVE' | 'SET_ONLY';
  value: BluetoothEntry;
  status?: DongleStatusType;
}

interface DeviceModalProps {
  isOpen: boolean;
  closeModal: () => void;
}

enum ModalStates {
  PREPARATION,
  ANALYSING,
  ANALYSISERROR,
  ANALYSISDONE,
  SAVING,
}

interface AnalysisButtonProps {
  state: ModalStates;
  beginAnalysis: () => void;
}
const _AnalysisButton = (props: AnalysisButtonProps) => {
  const { state, beginAnalysis } = props;
  if (state === ModalStates.ANALYSISDONE) {
    return (
      <IonButton expand="block" color="success">
        <IonIcon icon={checkmark} />
        <IonButton color="secondary">
          <Trans i18nKey="ANALYZE">Analysieren</Trans>
        </IonButton>
      </IonButton>
    );
  }
  if (state === ModalStates.ANALYSING) {
    return (
      <IonButton expand="block" disabled>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <IonSpinner name="crescent" style={{ width: 48, height: 48 }} />
        </div>
      </IonButton>
    );
  } else {
    return (
      <IonButton fill="solid" color="secondary" onClick={beginAnalysis}>
        <Trans i18nKey="ANALYZE" />
      </IonButton>
    );
  }
};

const isWeb =
  typeof window !== 'undefined' &&
  (!window.cordova || (window as any).Device?.platform === 'web');

const DeviceModal: React.FC<DeviceModalProps> = observer(
  ({ isOpen, closeModal }) => {
    const [accordionValue, setAccordionValue] = useState('first');

    const { location, resetLocation } = useLocation();
    const [present] = useIonToast();

    const estimator = useCalculationsAPI();
    const fsm = useFSM();
    const emptyDonglesRef = useRef<Record<string, DongleStatusType>>({});

    const [toast, dismiss] = useIonToast();
    const { recordList, currentUser } = useStores();
    const history = useHistory();
    const [isScanning, setIsScanning] = useState(false);

    // Reset scanning state when modal opens
    useEffect(() => {
      if (isOpen) {
        setIsScanning(false);
        setNoDeviceFound(false);
      }
    }, [isOpen]);

    const [allowedMACs, setAllowedMACs] = useState<string[]>();
    useEffect(() => {
      if (!currentUser?.freak1) return;
      setAllowedMACs(currentUser.freak1);
    }, [currentUser?.freak1, setAllowedMACs]);

    const [modalState, setModalState] = useState<ModalStates>(
      ModalStates.PREPARATION
    );
    const [trickTitle, setRunTitle] = useState<string | null>(null);
    const defaultRunTitle = `Run #${recordList.numRuns + 1}`;
    useIonViewDidEnter(() => {
      resetLocation();
    });

    const [device, setDevice] = useState<BluetoothInfo | null>(() =>
      fsm.bleDevice.get()
    );
    // Auswahl-Index für SensorCards
    const [selectedSensorId, setSelectedSensorId] = useState<string | null>(
      fsm.bleDevice.get()?.entry?.id || null
    );

    const [noDeviceFound, setNoDeviceFound] = useState(false);
    const [scanTimeout, setScanTimeout] = useState<NodeJS.Timeout | null>(null);

    const handleScanClick = useCallback(() => {
      if (isScanning) {
        if (scanTimeout) {
          clearTimeout(scanTimeout);
          setScanTimeout(null);
        }
        setIsScanning(false);
        setNoDeviceFound(false);
        return;
      }

      if (scanTimeout) {
        clearTimeout(scanTimeout);
        setScanTimeout(null);
      }
      setIsScanning(true);
      setNoDeviceFound(false);

      const timeout = setTimeout(() => {
        setNoDeviceFound(true);
        setIsScanning(false);
        setScanTimeout(null);
      }, 6000) as unknown as NodeJS.Timeout;
      setScanTimeout(timeout);

      setTimeout(() => {
        try {
          fsm.findSensors([NAME_FREAK, 'SPINNAX']).subscribe({
            next: async (entry: BluetoothEntry) => {
              if (scanTimeout) {
                clearTimeout(scanTimeout);
                setScanTimeout(null);
              }
              setNoDeviceFound(false);

              // HIER ANPASSEN:
              let response;
              if (isWeb) {
                // Im Web: Dummy-Status zurückgeben
                response = { text: 'demo' }; // erlaubter Wert!
              } else {
                response = await estimator.checkDongleStatus(entry.mac!);
              }
              if (!response || response.text === 'bound') {
                return;
              }

              if (device?.entry?.id === entry.id) {
                return;
              }

              newSensor({
                cmd: 'ADD',
                value: entry,
                status: response.text as DongleStatusType,
              });
            },
            error: (error) => {
              if (error.toString().includes('cordova_not_available')) {
                present({
                  message: 'BLE not available',
                  duration: MESSAGE_ALIVE_MS,
                  color: 'danger',
                  position: MESSAGE_POSITION,
                });
              } else {
                setIsScanning(false);
                setNoDeviceFound(true);
                if (scanTimeout) {
                  clearTimeout(scanTimeout);
                  setScanTimeout(null);
                }
              }
            },
            complete: () => {
              setIsScanning(false);
            },
          });
        } catch (error) {
          setIsScanning(false);
          setNoDeviceFound(true);
          if (scanTimeout) {
            clearTimeout(scanTimeout);
            setScanTimeout(null);
          }
        }
      }, 100);
    }, [device, fsm, scanTimeout, estimator]);

    useEffect(() => {
      if (device && device.entry) {
        setNoDeviceFound(false);
        setIsScanning(false);
        if (scanTimeout) {
          clearTimeout(scanTimeout);
          setScanTimeout(null);
        }
      }
    }, [device, scanTimeout]);

    useEffect(() => {
      return () => {
        if (scanTimeout) {
          clearTimeout(scanTimeout);
          setScanTimeout(null);
        }
      };
    }, [scanTimeout]);

    // Modal close cleanup
    useEffect(() => {
      if (!isOpen && scanTimeout) {
        clearTimeout(scanTimeout);
        setScanTimeout(null);
        setNoDeviceFound(false);
      }
    }, [isOpen, scanTimeout]);

    // Ref um zu verfolgen, ob wir nach dem Öffnen bereits gescannt haben
    const hasScannedOnOpenRef = useRef(false);

    // // Automatisch scannen wenn Modal geöffnet wird - nur einmal
    // useEffect(() => {
    //   if (isOpen && !isScanning && !hasScannedOnOpenRef.current) {
    //     hasScannedOnOpenRef.current = true;
    //     handleScanClick();
    //   }
    //   // Reset wenn Modal geschlossen wird
    //   if (!isOpen) {
    //     hasScannedOnOpenRef.current = false;
    //   }
    // }, [isOpen, isScanning, handleScanClick]);

    const accordionChanged: ComponentProps<
      typeof IonAccordionGroup
    >['onIonChange'] = useCallback(
      (
        event: IonAccordionGroupCustomEvent<AccordionGroupChangeEventDetail>
      ) => {
        setAccordionValue(event.detail.value);
      },
      [setAccordionValue]
    );

    const [foundSensors, newSensor] = useReducer(
      (
        state: Array<BluetoothInfo>,
        action: BluetoothEntryAction & { autoConnect?: boolean }
      ) => {
        const { cmd, value, status, autoConnect } = action;
        if (cmd === 'SET_ONLY') {
          return [{ entry: value, info: null, autoConnect: true }];
        }
        if (cmd === 'ADD') {
          const filtered = state.filter((val) => val.entry?.id !== value.id);
          if (status === 'nonexistent')
            emptyDonglesRef.current[value.id] = status;
          return [
            ...filtered,
            { entry: value, info: null, autoConnect: false },
          ];
        }
        return state;
      },
      []
    );

    useEffect(() => {
      const unsubscriber: Array<Lambda> = [];
      unsubscriber.push(
        observe(
          fsm.bleDevice,
          (changed: IValueDidChange<BluetoothInfo | null>) => {
            const newVal = toJS(changed.newValue as BluetoothInfo | null);
            setDevice(newVal);
            setSelectedSensorId(newVal?.entry?.id || null);
          },
          true
        )
      );

      return () => {
        unsubscriber.forEach((cb) => cb());
      };
    }, [fsm, fsm.bleState]);

    // Connect/Disconnect-Logik bleibt, aber wird oben im Modal verwendet
    const disconnect = useCallback(
      (id: string) => {
        runInAction(() => {
          fsm.setDeviceId(id);
        });
        fsm.setUserInputBle(StatesBLE.DISCONNECTED);
        runInAction(() => {
          fsm.setDeviceId('');
        });
      },
      [fsm]
    );

    const connect = useCallback(
      async (id: string, mac: string) => {
        let allowed = true;
        if (emptyDonglesRef.current[id])
          try {
            allowed = await currentUser!.addDongle(mac);
          } catch (error) {
            present({
              message: 'Error while adding dongle',
              duration: MESSAGE_ALIVE_MS,
              color: 'danger',
              position: MESSAGE_POSITION,
            });
            return;
          }
        if (allowed) {
          try {
            fsm.disconnectFromSensor();
          } catch (error) {
            // Error handled silently
          }
          runInAction(() => {
            fsm.setDeviceId(id);
          });
          // Nach erfolgreichem Verbindungsversuch nur noch diesen Freak in der Liste lassen
          newSensor({ cmd: 'SET_ONLY', value: { id, mac } as BluetoothEntry });
        }
        fsm.setUserInputBle(StatesBLE.CONNECTING);
      },
      [fsm, currentUser]
    );

    // Reihenfolge der gefundenen Sensoren beibehalten, keine Manipulation
    const sortedSensors = foundSensors;

    // Im DataContent: autoConnect-Flag auswerten und gelb färben
    const DataContent = () => {
      if (modalState === ModalStates.ANALYSISDONE) {
        return null;
      }
      // Handler für Klick auf SensorCard
      const handleSensorClick = async (sensor: BluetoothInfo) => {
        const connectedId = fsm.bleDevice.get()?.entry?.id;
        const isConnected =
          connectedId === sensor.entry?.id &&
          fsm.bleState === StatesBLE.CONNECTED;
        // Immer selektieren, auch wenn disconnect
        setSelectedSensorId(sensor.entry!.id);
        if (isConnected) {
          disconnect(sensor.entry!.id);
          return;
        }
        // Wenn ein anderer verbunden ist, erst disconnect, dann connect
        if (
          fsm.bleState === StatesBLE.CONNECTED &&
          connectedId &&
          connectedId !== sensor.entry?.id
        ) {
          disconnect(connectedId);
          setTimeout(() => {
            connect(sensor.entry!.id, sensor.entry!.mac!);
          }, 400);
          return;
        }
        // Wenn nicht verbunden, einfach verbinden
        if (fsm.bleState !== StatesBLE.CONNECTING && sensor.entry?.mac) {
          connect(sensor.entry!.id, sensor.entry!.mac!);
        }
      };
      return (
        <div style={{ width: '100%', height: '100%' }}>
          {/* SensorCard-Liste ohne Button, auswählbar */}
          <IonAccordionGroup
            onIonChange={accordionChanged}
            style={{
              marginLeft: '8px',
              marginRight: '8px',
              gap: '8px',
              display: 'flex',
              flexDirection: 'column',
            }}
            value={accordionValue}
          >
            <IonAccordion value="first" id="accordion">
              <div slot="content">
                <div
                  style={{
                    maxHeight: '100vh',
                    overflowY: 'auto',
                  }}
                >
                  {sortedSensors.map((sensor) => {
                    const isConnected =
                      fsm.bleDevice.get()?.entry?.id === sensor.entry?.id &&
                      fsm.bleState === StatesBLE.CONNECTED;
                    const isConnecting =
                      fsm.bleDevice.get()?.entry?.id === sensor.entry?.id &&
                      fsm.bleState === StatesBLE.CONNECTING;
                    const isSelected = sensor.entry?.id === selectedSensorId;
                    // Gelb, wenn autoConnect aktiv und nicht connected
                    const isAutoConnecting = sensor.autoConnect && !isConnected;
                    // Firmware-Info aus device, falls IDs übereinstimmen
                    const info =
                      fsm.bleDevice.get()?.entry?.id === sensor.entry?.id
                        ? fsm.bleDevice.get()?.info
                        : undefined;
                    return (
                      <div
                        key={sensor.entry!.id}
                        onClick={() => handleSensorClick(sensor)}
                        style={{
                          borderRadius: 8,
                          marginBottom: 8,
                          cursor: 'pointer',
                          transition: 'background 0.2s',
                        }}
                      >
                        <SensorCard
                          device={sensor}
                          batteryLevel={fsm.batteryLevel.get()}
                          isConnecting={isConnecting}
                          isConnected={isConnected}
                          isSelected={isSelected}
                          info={info}
                          // Neu: gelb, wenn autoConnect
                          highlightYellow={isAutoConnecting}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </IonAccordion>
          </IonAccordionGroup>

          {/* Nachricht wenn kein Gerät gefunden */}
          {noDeviceFound && (
            <IonCard className="no-device-card" style={{ margin: '20px' }}>
              <IonCardContent>
                <IonText color="dark">
                  <h3 style={{ textAlign: 'left', color: 'black' }}>
                    <Trans i18nKey="LOOKING_FOR_FREAK">
                      You failed to connect to your FRREAK? No worries – try
                      going through this checklist step by step:
                    </Trans>
                  </h3>
                  <ul
                    style={{
                      listStyle: 'none',
                      paddingLeft: '0px',
                      textAlign: 'left',
                    }}
                  >
                    <li
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: 'black',
                      }}
                    >
                      <IonIcon icon={checkmark} color="primary" />
                      <Trans i18nKey="PRESS_SCAN_AGAIN">
                        Wenn du dein FREAK nicht findest, drück nochmal auf
                        "Connect" manchmal braucht es einen zweiten Versuch.
                      </Trans>
                    </li>
                    <li
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: 'black',
                      }}
                    >
                      <IonIcon icon={checkmark} color="primary" />
                      <Trans i18nKey="CHECK_BLINKING">
                        If your FREAK ain't blinkin', it's probably just low on
                        juice – give it a charge.
                      </Trans>
                    </li>
                    <li
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: 'black',
                      }}
                    >
                      <IonIcon icon={checkmark} color="primary" />
                      <Trans i18nKey="TRY_LOGOUT">
                        Try logging out and back in – sometimes your setup just
                        needs a fresh push
                      </Trans>
                    </li>
                    <li
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: 'black',
                      }}
                    >
                      <IonIcon icon={checkmark} color="primary" />
                      <Trans i18nKey="RESTART_APP">
                        Close the app and pop it back open – sometimes a quick
                        restart gets the session rolling again.
                      </Trans>
                    </li>
                    <li
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: 'black',
                      }}
                    >
                      <IonIcon icon={checkmark} color="primary" />
                      <Trans i18nKey="CONTACT_SUPPORT">
                        Still not working? Hit up info@spinnax.com – we'll get
                        you back on track.
                      </Trans>
                    </li>
                  </ul>
                </IonText>
              </IonCardContent>
            </IonCard>
          )}
        </div>
      );
    };

    // // Auto-connect zu lastKnown freak nach Scan-Ende, nicht beim Öffnen
    // useEffect(() => {
    //   // Nur nach Scan-Ende prüfen
    //   if (isScanning) return;
    //   const lastKnownId = fsm.bleDevice.get()?.entry?.id;
    //   let resetTimeout: NodeJS.Timeout | undefined;
    //   if (fsm.bleState === StatesBLE.DISCONNECTED && lastKnownId) {
    //     console.log('[DeviceModal] Auto-connect: Setting userInputBle to CONNECTING for lastKnownId', lastKnownId);
    //     runInAction(() => {
    //       fsm.setUserInputBle && fsm.setUserInputBle(StatesBLE.CONNECTING);
    //     });
    //     // Nach 5s zurücksetzen, falls immer noch CONNECTING
    //     resetTimeout = setTimeout(() => {
    //       if (fsm.bleState === StatesBLE.CONNECTING) {
    //         console.log('[DeviceModal] Auto-connect: Timeout, resetting BLE state to DISCONNECTED');
    //         runInAction(() => {
    //           fsm.setUserInputBle && fsm.setUserInputBle(StatesBLE.DISCONNECTED);
    //         });
    //       }
    //     }, 5000);
    //   } else {
    //     console.log('[DeviceModal] No auto-connect: bleState', fsm.bleState, 'lastKnownId', lastKnownId);
    //   }
    //   return () => {
    //     if (resetTimeout) clearTimeout(resetTimeout);
    //   };
    // }, [isScanning, fsm.bleState, fsm.bleDevice.get()?.entry?.id]);

    // Nach Scan-Ende: Zuletzt bekannten Freak zur Liste hinzufügen, falls nicht gefunden
    useEffect(() => {
      if (!isScanning) {
        const lastKnown = fsm.bleDevice.get();
        if (lastKnown && lastKnown.entry && lastKnown.entry.id) {
          const alreadyInList = foundSensors.some(
            (s) => s.entry?.id === lastKnown.entry!.id
          );
          if (!alreadyInList) {
            newSensor({
              cmd: 'ADD',
              value: lastKnown.entry,
              status: undefined,
            });
            // Direkt verbinden
            if (lastKnown.entry.mac) {
              runInAction(() => {
                fsm.setUserInputBle &&
                  fsm.setUserInputBle(StatesBLE.CONNECTING);
              });
              // connect-Funktion aus dem Scope holen
              if (typeof connect === 'function') {
                connect(lastKnown.entry.id, lastKnown.entry.mac);
              }
            }
          }
        }
      }
    }, [isScanning, fsm.bleDevice.get()?.entry?.id]);

    return (
      <IonModal
        isOpen={isOpen}
        onAbort={closeModal}
        onDidDismiss={closeModal}
        className="edit-modal"
        backdropDismiss={false}
      >
        <IonHeader className="ion-no-border">
          <IonToolbar className="edit-modal-toptoolbar">
            <IonButtons slot="start">
              <DynamicButton
                iconName="FreakSearchUpIcon"
                buttonType="icon-full"
                fill="clear"
                size="xlarge"
                color="primary"
                className="spnx-button"
                id="scan-button"
                showSpinner={isScanning}
                onClick={() => {
                  if (!isScanning) handleScanClick();
                }}
              />
            </IonButtons>

            <IonTitle style={{ textAlign: 'center' }}>
              <Trans i18nKey="CLICK_TO_CONNECT">Click to connect</Trans>
            </IonTitle>
            <IonButtons slot="end">
              <DynamicButton
                text={<Trans i18nKey="CANCEL" />}
                iconName="CancelUpIcon"
                buttonType="icon"
                size="xlarge"
                fill="clear"
                className="spnx-button"
                onClick={closeModal}
              />
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent
          style={{ '--background': 'transparent', '--border-colour': 0 }}
        >
          <div>
            <DataContent />
          </div>
        </IonContent>
      </IonModal>
    );
  }
);

export default DeviceModal;
